import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, IdCard } from 'lucide-react';
import { getMiPerfil, type MotivoCarga } from '@lib/services/perfil.service';
import { useAuthStore } from '@lib/store/auth.store';
import { TableSkeleton } from '@components/medicus-ui/Loaders';

/**
 * Mi perfil. Hoy muestra el perfil de PROMOTOR: con qué usuario de Binary está
 * asociada la cuenta y de qué empresas puede cargar padrones.
 *
 * La habilitación no se configura acá ni se guarda en el micrositio: sale de
 * Binary (`SA_PROMOTORES_EMPLEADORES`), que es donde la administra el negocio.
 * Por eso la pantalla es de solo lectura y cada fila explica por qué está o no
 * habilitada, en vez de mostrar un "no" a secas.
 */
/** Por qué puede cargar, en palabras. Las claves son las del server. */
const MOTIVO_CARGA: Record<MotivoCarga, string> = {
  'permiso-rol': 'por tu rol',
  'promotor-binary': 'sos promotor vigente en Binary',
  'permiso-manual': 'permiso otorgado por Medicus',
  'sin-verificar-binary': 'sin verificar contra Binary (Oracle no respondió)',
  'denegado-manual': '',
  'no-es-promotor': '',
  'sin-promotor-cargado': '',
  'sin-plan': '',
};

export function PerfilPage() {
  const { user } = useAuthStore();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['mi-perfil'],
    queryFn: getMiPerfil,
  });

  const habilitadas = data?.empresas.filter((e) => e.puedeCargar).length ?? 0;

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IdCard size={22} style={{ color: 'var(--color-primary-darker)' }} /> Mi perfil
        </h1>
        <p className="text-element-mid">
          Tus datos de promotor en Binary y de qué organizaciones podés cargar padrones.
        </p>
      </header>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2 className="heading-5" style={{ marginTop: 0 }}>
          Cuenta
        </h2>
        <Dato etiqueta="Nombre" valor={user?.nombre} />
        <Dato etiqueta="Email" valor={user?.email} />
      </div>

      {isLoading && <TableSkeleton />}

      {isError && (
        <p className="status status-error" style={{ marginTop: '1rem' }}>
          No se pudo cargar el perfil. {error instanceof Error ? error.message : ''}
        </p>
      )}

      {data && (
        <>
          {/* El diagnóstico solo aparece cuando Binary no respondió. Sin esto,
              una caída de Oracle se leería como "no sos promotor de nada". */}
          {data.diagnostico && (
            <p
              className="status status-warning"
              style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <AlertTriangle size={16} /> {data.diagnostico}
            </p>
          )}

          <div className="card" style={{ marginTop: '1rem' }}>
            <h2 className="heading-5" style={{ marginTop: 0 }}>
              Promotor en Binary
            </h2>
            {data.binaryUsername ? (
              <>
                <Dato etiqueta="Usuario" valor={data.binaryUsername} />
                <Dato etiqueta="Nombre" valor={data.promotor?.apellidoNombre} />
                <Dato etiqueta="Legajo" valor={data.promotor?.legajo} />
                <Dato etiqueta="Tipo" valor={data.promotor?.tipo} />
                <Dato
                  etiqueta="Estado"
                  valor={data.promotor?.estado === 'A' ? 'Activo' : data.promotor?.estado}
                />
                {!data.promotor && !data.diagnostico && (
                  <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 0 }}>
                    El usuario <strong>{data.binaryUsername}</strong> no figura en el maestro de
                    promotores de Binary. Podés cargar padrones igual de las organizaciones donde
                    esté habilitado, pero conviene avisarle a Medicus.
                  </p>
                )}
              </>
            ) : (
              <p className="text-element-mid" style={{ marginBottom: 0 }}>
                Tu cuenta no tiene asociado un usuario de Binary. Solo hace falta para cargar
                padrones de organizaciones donde figurás como promotor; las de tu rol las cargás
                igual (abajo dice cuáles).
              </p>
            )}
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <div className="rowBetween" style={{ marginBottom: '0.75rem' }}>
              <h2 className="heading-5" style={{ margin: 0 }}>
                Organizaciones
              </h2>
              <span className="text-element-mid" style={{ fontSize: 13 }}>
                {habilitadas} de {data.empresas.length}{' '}
                {data.empresas.length === 1 ? 'habilitada' : 'habilitadas'} para cargar padrones
              </span>
            </div>

            {data.empresas.length === 0 ? (
              <p className="text-element-mid" style={{ marginBottom: 0 }}>
                No tenés organizaciones asignadas.
              </p>
            ) : (
              <div className="tableWrap">
                <table className="dataTable">
                  <thead>
                    <tr>
                      <th>Organización</th>
                      <th>Empleador</th>
                      <th>Padrones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.empresas.map((e) => (
                      <tr key={e.empresaId}>
                        <td>{e.nombre}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {e.oracleEmplId ?? '—'}
                        </td>
                        <td>
                          {e.puedeCargar ? (
                            <>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  color: '#166534',
                                  fontWeight: 600,
                                }}
                              >
                                <BadgeCheck size={15} /> Podés cargar
                              </span>
                              <div
                                style={{
                                  fontSize: 11.5,
                                  color: 'var(--color-element-mid)',
                                  marginTop: 2,
                                }}
                              >
                                {MOTIVO_CARGA[e.motivoCarga]}
                                {e.promotorUsername && ` · promotor ${e.promotorUsername}`}
                              </div>
                            </>
                          ) : (
                            <>
                              <span style={{ color: 'var(--color-element-mid)' }}>
                                No habilitado
                              </span>
                              {(e.detalleCarga ?? e.motivo) && (
                                <div
                                  style={{
                                    fontSize: 11.5,
                                    color: 'var(--color-element-mid)',
                                    marginTop: 2,
                                  }}
                                >
                                  {e.detalleCarga ?? e.motivo}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor?: string | null }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 14 }}>
      <span className="text-element-mid" style={{ minWidth: 90 }}>
        {etiqueta}
      </span>
      <strong>{valor || '—'}</strong>
    </div>
  );
}
